/**
 * Connect Protocol Service Clients
 * 
 * This file provides typed service clients for all Guardian AI gRPC services
 * using the Connect Protocol. All clients use the unified transport layer.
 * 
 * Usage:
 *   import { authClient, trustScoreClient } from '@/api/grpc/services';
 *   
 *   // Call a method
 *   const response = await authClient.login({ username: 'user', password: 'pass' });
 * 
 * @see https://connectrpc.com/docs/web/using-clients
 */

import { createClient, type Client } from '@connectrpc/connect';
import { transport } from './transport';

// Import service descriptors from generated protobuf code - ALL from v1 canonical location
// Auth & User
import { AuthService } from '@/proto/gen/guardianai/v1/auth/auth_pb';
import { TrustScoreService } from '@/proto/gen/guardianai/v1/trust/trust_pb';
import { UserService } from '@/proto/gen/guardianai/v1/user/user_pb';

// Fraud detection & reporting
import { FraudReportService } from '@/proto/gen/guardianai/v1/fraud/fraud_pb';

// Payment, subscriptions, credits
import { PaymentService, SubscriptionService, ScanCreditService } from '@/proto/gen/guardianai/v1/payment/payment_pb';

// Commission & Payout Services
import { CommissionConfigService, PayoutService } from '@/proto/gen/guardianai/v1/payment/commission_pb';

// Disputes & mediation
import { DisputeService, AdminDashboardService } from '@/proto/gen/guardianai/v1/dispute/dispute_pb';

// Earnings & payouts
import { EarningsService } from '@/proto/gen/guardianai/v1/earnings/earnings_pb';

// Compliance, audit, consent
import { AuditService, ConsentService, ScanConsentService } from '@/proto/gen/guardianai/v1/compliance/compliance_pb';

// Contact
import { ContactService } from '@/proto/gen/guardianai/v1/contact/contact_pb';

// Marketplace
import { CertificationAuthorityService } from '@/proto/gen/guardianai/v1/marketplace/certification_pb';
// Admin Ad Funnels & Security Config
import { AdFunnelService } from '@/proto/gen/guardianai/v1/admin/ad_funnel_pb';
import { SecurityConfigService } from '@/proto/gen/guardianai/v1/admin/security_config_pb';

// High-Trust Cybersecurity Marketplace Services
import { ExpertScanService } from '@/proto/gen/guardianai/v1/expert/expert_scan_pb';
import { HrManagerService, MagicLinkService } from '@/proto/gen/guardianai/v1/hr/hr_manager_pb';

// Media Proof & Assets
import { MediaProofService, RagIntegritySentinelService } from '@/proto/gen/guardianai/v1/admin/media_proof_pb';
import { AssetService } from '@/proto/gen/guardianai/v1/admin/assets_pb';
import { ResearchPaperService } from '@/proto/gen/guardianai/v1/admin/research_papers_pb';
import { AdminMarketingService } from '@/proto/gen/guardianai/v1/admin/blog_pb';

// Public Marketing Service
import { PublicMarketingService } from '@/proto/gen/guardianai/v1/public/marketing_pb';

// Demo Link Management Services (GDDE Wave 1)
import { DemoAdminService, ConsultationService } from '@/proto/gen/guardianai/v1/demo/demo_pb';

// Guardian Infra License Management
import { InfraLicenseService } from '@/proto/gen/guardianai/v1/infra/infra_license_pb';

// Unified Pricing Administration
import { PricingAdminService } from '@/proto/gen/guardianai/v1/admin/pricing_pb';

// NEW v1 consolidated services
// Admin & Work Management
import { AdminService, WorkManagementService } from '@/proto/gen/guardianai/v1/admin/admin_pb';

// Analytics
import { AnalyticsService } from '@/proto/gen/guardianai/v1/analytics/analytics_pb';

// Evidence Marketplace & Anonymous Orders
import { EvidenceMarketplaceService, AnonymousOrderService } from '@/proto/gen/guardianai/v1/marketplace/evidence_pb';

// ML Service
import { MLService } from '@/proto/gen/guardianai/v1/ml/ml_pb';

// Collaboration
import { CollaborationService } from '@/proto/gen/guardianai/v1/collab/collab_pb';

// Search
import { SearchService } from '@/proto/gen/guardianai/v1/search/search_pb';

// Referral Sales
import { AdminReferralSalesService, SalesAgentService, PublicReferralService } from '@/proto/gen/guardianai/v1/referral/referral_sales_pb';
import { CaptionService } from '@/proto/gen/guardianai/v1/caption/caption_pb';
import { DistroEngineService } from '@/proto/gen/guardianai/v1/distro/distro_pb';
import {
  AdminFamilyCareService,
  ParentFamilyCareService,
  PublicFamilyCareService,
  WorkerFamilyCareService,
} from '@/proto/gen/guardianai/v1/familycare/family_care_pb';
import { GovernableAiWorkspaceService } from '@/proto/gen/guardianai/v1/governableai/governable_ai_pb';

// Booster Packages
import { BoosterService } from '@/proto/gen/guardianai/v1/booster/booster_pb';

// Compliance (use ConsentService as ComplianceService replacement for legacy compat)
const ComplianceService = ConsentService;

// =============================================================================
// Type Exports
// =============================================================================

// Re-export commonly used types for convenience
;

;

// =============================================================================
// Service Client Instances
// =============================================================================

/**
 * Authentication service client
 * Handles: login, logout, register, password reset, 2FA
 */
export const authClient: Client<typeof AuthService> = createClient(AuthService, transport);

/**
 * Trust Score service client
 * Handles: pre-registration scan, post-registration scan, trust score calculation
 */
const trustScoreClient: Client<typeof TrustScoreService> = createClient(TrustScoreService, transport);

/**
 * User service client
 * Handles: user profiles, preferences, account management
 */
export const userClient: Client<typeof UserService> = createClient(UserService, transport);

/**
 * Payment service client
 * Handles: payment processing, transaction history
 */
const paymentClient: Client<typeof PaymentService> = createClient(PaymentService, transport);

/**
 * Subscription service client
 * Handles: subscription plans, billing
 */
const subscriptionClient: Client<typeof SubscriptionService> = createClient(SubscriptionService, transport);

/**
 * Scan Credit service client
 * Handles: scan credits, usage tracking
 */
const scanCreditClient: Client<typeof ScanCreditService> = createClient(ScanCreditService, transport);

/**
 * Fraud Report service client
 * Handles: fraud reports, investigations
 */
const fraudReportClient: Client<typeof FraudReportService> = createClient(FraudReportService, transport);

/**
 * Admin service client
 * Handles: administrative operations, system management
 */
const adminClient: Client<typeof AdminService> = createClient(AdminService, transport);

/**
 * Ad Funnel client
 * Handles: creating and tracking marketing funnels
 */
export const adFunnelClient: Client<typeof AdFunnelService> = createClient(AdFunnelService, transport);

/**
 * Security Config client
 * Handles: dynamically fetching global UI security policies
 */
export const securityConfigClient: Client<typeof SecurityConfigService> = createClient(SecurityConfigService, transport);

/**
 * Media Proof Config client
 * Handles: Media proof page states and waitlist
 */
export const mediaProofClient: Client<typeof MediaProofService> = createClient(MediaProofService, transport);

/**
 * RAG Integrity Sentinel Config client
 * Handles: RAG integrity page states and waitlist
 */
export const ragIntegrityClient: Client<typeof RagIntegritySentinelService> = createClient(
  RagIntegritySentinelService,
  transport
);

/**
 * Asset Upload client
 * Handles: Uploading assets to Google Cloud Storage
 */
export const assetClient: Client<typeof AssetService> = createClient(AssetService, transport);

/**
 * Research Papers Management client
 * Handles: CRUD for published research papers
 */
export const researchPaperClient: Client<typeof ResearchPaperService> = createClient(ResearchPaperService, transport);

/**
 * Marketing CMS (Admin) client
 */
export const adminMarketingClient: Client<typeof AdminMarketingService> = createClient(AdminMarketingService, transport);

/**
 * Public Marketing client
 */
export const publicMarketingClient: Client<typeof PublicMarketingService> = createClient(PublicMarketingService, transport);

/**
 * Work Management service client
 * Handles: work assignments, task management
 */
export const workManagementClient: Client<typeof WorkManagementService> = createClient(WorkManagementService, transport);

/**
 * Analytics service client
 * Handles: analytics data, reporting
 */
const analyticsClient: Client<typeof AnalyticsService> = createClient(AnalyticsService, transport);

/**
 * Evidence Marketplace service client
 * Handles: evidence management, chain of custody
 */
export const evidenceMarketplaceClient: Client<typeof EvidenceMarketplaceService> = createClient(EvidenceMarketplaceService, transport);

/**
 * Anonymous Order service client
 * Handles: anonymous evidence orders
 */
export const anonymousOrderClient: Client<typeof AnonymousOrderService> = createClient(AnonymousOrderService, transport);

export const disputeClient: Client<typeof DisputeService> = createClient(DisputeService, transport);

/**
 * Admin Dashboard service client
 * Handles: combined admin dashboard stats, activity, alerts
 */
export const adminDashboardClient: Client<typeof AdminDashboardService> = createClient(AdminDashboardService, transport);

/**
 * Certification Authority service client
 * Handles: certifications, credentials
 */
export const certificationClient: Client<typeof CertificationAuthorityService> = createClient(CertificationAuthorityService, transport);

/**
 * ML service client
 * Handles: machine learning operations, model inference
 */
export const mlClient: Client<typeof MLService> = createClient(MLService, transport);

/**
 * Audit service client
 * Handles: audit logging, compliance tracking
 */
const auditClient: Client<typeof AuditService> = createClient(AuditService, transport);

/**
 * Collaboration service client
 * Handles: team collaboration, shared resources
 */
const collaborationClient: Client<typeof CollaborationService> = createClient(CollaborationService, transport);

/**
 * Compliance service client
 * Handles: regulatory compliance, policy enforcement
 */
const complianceClient: Client<typeof ComplianceService> = createClient(ComplianceService, transport);

/**
 * Scan Consent service client
 * Handles: trust score scan consent, email verification
 */
const scanConsentClient: Client<typeof ScanConsentService> = createClient(ScanConsentService, transport);

/**
 * Search service client
 * Handles: entity search, global search
 */
const searchClient: Client<typeof SearchService> = createClient(SearchService, transport);

/**
 * Earnings service client
 * Handles: earnings balance, withdrawals, payouts, Stripe Connect onboarding
 */
export const earningsClient: Client<typeof EarningsService> = createClient(EarningsService, transport);

/**
 * Commission Config service client
 * Handles: platform commission rates, HR commission rates, payout thresholds, commission history
 * 
 * Available RPCs:
 * - GetCommissionConfig: Get current global commission configuration
 * - UpdatePlatformCommission: Update platform commission rate (admin only)
 * - UpdateDefaultHrCommission: Update default HR manager commission rate (admin only)
 * - GetCommissionHistory: Get audit log of commission changes
 */
export const commissionConfigClient: Client<typeof CommissionConfigService> = createClient(CommissionConfigService, transport);

/**
 * Payout service client
 * Handles: Stripe Connect integration, payout requests, payout status, earnings breakdown
 * 
 * Available RPCs:
 * - InitiateStripeConnect: Start Stripe Connect OAuth flow for user
 * - CompleteStripeConnect: Complete OAuth callback and link account
 * - GetStripeAccountStatus: Check user's Stripe account status
 * - RequestPayout: Request a payout to connected Stripe account
 * - GetPayoutStatus: Get status of a specific payout
 * - ListPayouts: List user's payout history
 * - GetEarningsSummary: Get earnings summary for user
 * - GetEarningsBreakdown: Get detailed earnings breakdown by job
 * - ProcessPendingPayouts: Admin batch payout processing
 * - AdminPayoutOverride: Admin override for payout status
 */
export const payoutClient: Client<typeof PayoutService> = createClient(PayoutService, transport);

/**
 * Contact service client
 * Handles: public contact form submissions
 */
export const contactClient: Client<typeof ContactService> = createClient(ContactService, transport);

// =============================================================================
// High-Trust Cybersecurity Marketplace Service Clients
// =============================================================================

/**
 * Expert Scan service client
 * Handles: job queue listing, job claiming, work progress, report submission
 * 
 * Available RPCs:
 * - ListAvailableJobs: Get open jobs for expert marketplace
 * - ClaimJob: Expert claims a job from the queue
 * - GetJobDetails: Get full job details including credentials
 * - ListMyAssignedJobs: Get expert's current jobs
 * - StartWork: Begin work on a claimed job
 * - SubmitReport: Submit completed scan report
 * - DeclineJob: Expert declines a claimed job
 */
export const expertScanClient: Client<typeof ExpertScanService> = createClient(ExpertScanService, transport);

/**
 * HR Manager service client
 * Handles: HR manager CRUD, expert management, slot limits, onboarding
 * 
 * Available RPCs:
 * - CreateHrManager: Admin creates new HR manager
 * - GetHrManager: Get HR manager details
 * - ListHrManagers: List all HR managers (admin)
 * - GetSlotUsage: Get HR manager's expert slot usage
 * - InviteExpert: Send magic link invitation to expert
 * - ListMyExperts: List experts under HR manager
 * - SuspendExpert/UnsuspendExpert: Expert management
 * - GetEarningsBreakdown: Get HR commission breakdown
 * - GetCommissionRate: Get HR manager's commission rate
 */
export const hrManagerClient: Client<typeof HrManagerService> = createClient(HrManagerService, transport);

/**
 * Magic Link service client
 * Handles: magic link validation, onboarding completion, magic link resend
 *
 * Available RPCs:
 * - ValidateMagicLink: Validate a magic link token and get role/email
 * - CompleteOnboarding: Complete onboarding with password, name, skills
 * - ResendMagicLink: Resend expired magic link
 * - GenerateMagicLink: Generate new magic link (admin)
 */
export const magicLinkClient: Client<typeof MagicLinkService> = createClient(MagicLinkService, transport);

// =============================================================================
// Demo Link Management Service Clients (GDDE Wave 1)
// =============================================================================

/**
 * Demo Admin service client
 * Handles: Demo link CRUD, analytics, event tracking
 * 
 * Available RPCs:
 * - CreateDemoLink: Create new shareable demo link
 * - GetDemoLink: Retrieve link by ID or slug
 * - UpdateDemoLink: Modify link properties
 * - ListDemoLinks: Paginated link listing
 * - DeactivateLink: Soft-delete link
 * - GetLinkAnalytics: Aggregated metrics and daily breakdown
 * - ExportLinkReport: Generate PDF report
 * - EmailReportToSales: Email report to recipient
 * - TrackDemoEvent: Record frontend analytics events
 * - GetDemoLinksSummary: Admin dashboard summary stats
 */
export const demoAdminClient: Client<typeof DemoAdminService> = createClient(DemoAdminService, transport);

/**
 * Consultation service client
 * Handles: $500 architecture consultation payments
 * 
 * Available RPCs:
 * - CreateCheckoutSession: Initiate Stripe payment
 * - HandleStripeWebhook: Process payment confirmations
 * - GetBooking: Retrieve booking details
 * - ListBookings: Paginated booking list
 * - UpdateBookingStatus: Update status and calendar info
 */
export const consultationClient: Client<typeof ConsultationService> = createClient(ConsultationService, transport);

// =============================================================================
// Guardian Infra License Management Service Client
// =============================================================================

/**
 * Infra License service client
 * Handles: License CRUD, subscription stats, pricing management, Stripe checkout
 * 
 * Available RPCs:
 * - GenerateLicense: Create new license with tier and customer info
 * - ValidateLicense: Verify license key signature and expiry
 * - GetLicense: Retrieve license by ID, key, or Stripe subscription
 * - ListLicenses: Paginated list with tier/status filters
 * - RevokeLicense: Deactivate a license with audit reason
 * - GetSubscriptionStats: Dashboard metrics (MRR, ARR, subscribers)
 * - GetPricing: Current Pro/Enterprise pricing
 * - UpdatePricing: Adjust pricing (admin only)
 * - CreateInfraCheckout: Start Stripe checkout session
 */
export const infraLicenseClient: Client<typeof InfraLicenseService> = createClient(InfraLicenseService, transport);

/**
 * Pricing Admin service client (Admin-only)
 * Handles: Scan credit packages, subscription plans, consultation pricing, Stripe sync
 * 
 * Available RPCs:
 * - ListScanCreditPackages, CreateScanCreditPackage, UpdateScanCreditPackage, DeleteScanCreditPackage
 * - ListSubscriptionPlans, UpdateSubscriptionPlan
 * - GetConsultationPricing, UpdateConsultationPricing
 * - SyncScanCreditPackagesToStripe, SyncSubscriptionPlansToStripe, SyncConsultationToStripe
 * - GetPricingSummary: Dashboard overview
 */
export const pricingAdminClient: Client<typeof PricingAdminService> = createClient(PricingAdminService, transport);

// =============================================================================
// Legacy Compatibility Layer
// These aliases allow existing code to work without immediate refactoring.
// All aliases are marked deprecated and should be migrated to the new names.
// =============================================================================

/**
 * @deprecated Use authClient directly
 */
export const authServiceClient = authClient;

/**
 * @deprecated Use trustScoreClient directly
 */
export const trustScoreServiceClient = trustScoreClient;

/**
 * @deprecated Use userClient directly
 */
export const userServiceClient = userClient;

/**
 * @deprecated Use paymentClient directly
 */
export const paymentServiceClient = paymentClient;

/**
 * @deprecated Use fraudReportClient directly
 */
export const fraudReportServiceClient = fraudReportClient;

/**
 * @deprecated Use disputeClient directly
 */
export const disputeServiceClient = disputeClient;

/**
 * @deprecated Use scanCreditClient directly
 */
export const scanCreditServiceClient = scanCreditClient;

/**
 * @deprecated Use subscriptionClient directly
 */
export const subscriptionServiceClient = subscriptionClient;

/**
 * @deprecated Use adminClient directly
 */
export const AdminServiceClient = adminClient;

/**
 * @deprecated Use analyticsClient directly
 */
export const analyticsServiceClient = analyticsClient;

/**
 * @deprecated Use auditClient directly
 */
export const auditServiceClient = auditClient;

/**
 * @deprecated Use collaborationClient directly
 */
export const collaborationServiceClient = collaborationClient;

/**
 * @deprecated Use complianceClient directly
 */
export const complianceServiceClient = complianceClient;

/**
 * @deprecated Use searchClient directly
 */
export const searchServiceClient = searchClient;

/**
 * @deprecated Use scanConsentClient directly
 */
export const scanConsentServiceClient = scanConsentClient;
/** Referral Sales Service Clients */
export const adminReferralSalesClient = createClient(AdminReferralSalesService, transport);
export const salesAgentClient = createClient(SalesAgentService, transport);

/** Public Referral Service Client (no auth required) */
export const publicReferralClient = createClient(PublicReferralService, transport);

/** CaptionEngine Service Client */
export const captionClient = createClient(CaptionService, transport);

/** DistroEngine Service Client */
export const distroEngineClient = createClient(DistroEngineService, transport);

/** BoosterService Client — Managed Growth/Brand campaigns */
export const boosterClient = createClient(BoosterService, transport);

/** Family Care public, parent, caregiver, and administrator clients. */
export const publicFamilyCareClient = createClient(PublicFamilyCareService, transport);
export const parentFamilyCareClient = createClient(ParentFamilyCareService, transport);
export const workerFamilyCareClient = createClient(WorkerFamilyCareService, transport);
export const adminFamilyCareClient = createClient(AdminFamilyCareService, transport);

/** Governable AI workspace client. */
export const governableAiWorkspaceClient = createClient(GovernableAiWorkspaceService, transport);
